import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {InfluencerGroupNew, InfluencerGroups, InfluencerGroup} from '../../../../models/influencer-group';
import {InfluencerGroupsService} from '../influencer-groups.service';
import {UserService} from '../../../services/user.service';
import {ReferenceDataService} from '../../../services/reference-data/reference-data.service';
import {PlaceholderVisibilityService} from '../../../services/placeholder-visibility/placeholder-visibility.service';
import {CommonConstant} from '../../../app.constants';
import {EventTrackerService, Page, UserActionType, ActivePages, ActionSource} from '../../../services/event-tracker/event-tracker.service';
import { ActivePage } from '../../../../models/event-tracker';
import {ErrorTemplate} from '../../../../models/ErrorTemplate';
import {ValidationHandlerService} from '../../../core/validation-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { GroupService } from '../../../services/group-details/group.service';
import { ObjectUtils } from '../../../utils/object.utils';

@Component({
  selector: 'create-group-fragment',
  templateUrl: './create-group-fragment.component.html',
  styleUrls: ['./create-group-fragment.component.scss']
})
export class CreateGroupFragmentComponent implements OnInit, AfterViewInit {

  @Output()
  public cancelCreateOrUpdateGroupEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public beforeCreateOrUpdateGroupEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  public afterCreateOrUpdateGroupEvent: EventEmitter<any> = new EventEmitter<any>();
  @Input()
  isShowCreateGroupForm: boolean;
  @Input()
  activePage: ActivePage;

  @Input()
  public groupNeedToUpdate: InfluencerGroup;

  public groupName: string;
  public groupDescription: string;
  public previousInfluencerGroupDetail: InfluencerGroups;

  constructor(private influenderGroupsService: InfluencerGroupsService,
              private userService: UserService,
              private referenceDataService: ReferenceDataService,
              private placeholderVisibilityService: PlaceholderVisibilityService,
              private validationService: ValidationHandlerService,
              private eventTracker: EventTrackerService,
              private translate: TranslateService,
              private groupService: GroupService) { }

  ngOnInit() {
    if (this.groupNeedToUpdate) {
      this.loadInfluencerGroupDetail();
    }
  }

  ngAfterViewInit() {
    const textarea = document.querySelector('.desc-field textarea') as HTMLTextAreaElement;
    textarea.addEventListener('keyup', () => {
      textarea.style.height = this.calcHeight(textarea.value) + 'px';
    });
  }

  private loadInfluencerGroupDetail() {
    this.previousInfluencerGroupDetail = this.groupNeedToUpdate ? ObjectUtils.deepClone(this.groupNeedToUpdate) : null;
    this.groupName = this.groupNeedToUpdate ? this.groupNeedToUpdate.influencer_group_name : null;
    this.groupDescription = this.groupNeedToUpdate ? this.groupNeedToUpdate.influencer_group_description : null;
  }

  createOrUpdateGroup() {
    if (this.groupNeedToUpdate) {
      this.updateGroup();
    } else {
      this.createNewGroup();
    }
  }

  private updateGroup() {
    const influencerGroupData = {
      companyId: this.userService.getCompanyId(),
      influencerGroupDescription: this.groupDescription,
      influencerGroupId: this.groupNeedToUpdate.influencer_group_id,
      influencerGroupName: this.groupName,
      influencerGroupType: this.groupNeedToUpdate.influencer_group_type,
      conditionListObject: this.groupService.normalizeGroupConditionList(this.groupNeedToUpdate.condition_list_object),
      influencerGroupTypeValues: this.groupNeedToUpdate.influencer_group_type_values
    };

    const errors: ErrorTemplate[] = this.influenderGroupsService.validateGroup(<any>influencerGroupData);
    if (errors && errors.length > 0) {
      this.validationService.shouldDisplayErrors(true);
      this.validationService.updateErrors(errors);
      return;
    }

    // optimistic UI, update group name and close bottombar
    this.beforeCreateOrUpdateGroupEvent.emit(influencerGroupData);
    this.groupService.updateInfluencerGroup(influencerGroupData).subscribe((res) => {
      this.afterCreateOrUpdateGroupEvent.emit({
        isError: false,
        data: influencerGroupData
      });
    }, (error) => {
      this.afterCreateOrUpdateGroupEvent.emit({
        isError: true,
        data: error
      });
      this.loadInfluencerGroupDetail();
    });
  }

  /**
   * everytime create a group successfully. We must register it to segment
   * @param {InfluencerGroupNew} influencerGroup
   */
  private sendEventToSegmentAfterCreatingGroupSuccess(influencerGroup: InfluencerGroupNew) {
    this.influenderGroupsService.getInfluencerGroups(this.userService.getCompanyId()).subscribe( (data) => {
      let newGroup;
      if (data && Array.isArray(data)) {
        newGroup = data.find(g => g.name === influencerGroup.influencerGroupName);
      }

      if (newGroup) {
        // track event to Segment
        this.eventTracker.trackUserAction(UserActionType.ACTION, Page.GROUP, {
          id: newGroup.id,
          name: newGroup.name,
          type: Page.GROUP
        }, {
          action: UserActionType.CREATE,
          source: ActionSource.ACTION_VIEW,
          ...influencerGroup
        }, undefined, {
          type: this.activePage && this.activePage.type || ActivePages.DISCOVER,
          name: this.activePage && this.activePage.name,
          id: this.activePage && this.activePage.id
        });
        this.eventTracker.trackUserAction(UserActionType.CHAM_GROUP_CREATE);
      }
    });
  }

  private createNewGroup() {
    const influencerGroup = this.prepareNewGroupData();
    const errors: ErrorTemplate[] = this.influenderGroupsService.validateGroup(influencerGroup);
    if (errors && errors.length > 0) {
      this.validationService.shouldDisplayErrors(true);
      this.validationService.updateErrors(errors);
      return;
    }
    this.beforeCreateOrUpdateGroupEvent.emit({
      data: {
        id: influencerGroup.influencerGroupId,
        name: this.groupName,
        description: this.groupDescription
      },
      isCreated: true
    });
    // show loader
    this.placeholderVisibilityService.show(CommonConstant.CREATE_NEW_GROUP_HOLDER);
    this.influenderGroupsService.createInfluencerGroup(influencerGroup).subscribe( (data) => {
      // execute callback after creating new group
      this.afterCreateOrUpdateGroupEvent.emit({
        data: {
          name: this.groupName,
          description: this.groupDescription
        },
        isCreated: true,
        isError: false
      });
      this.sendEventToSegmentAfterCreatingGroupSuccess(influencerGroup);
      // reset ngModel data
      this.groupName = null;
      this.groupDescription = null;
      // hide loader
      this.placeholderVisibilityService.hide(CommonConstant.CREATE_NEW_GROUP_HOLDER);
    }, (error) => {
      this.afterCreateOrUpdateGroupEvent.emit({
        data: {
          name: this.groupName,
          description: this.groupDescription
        },
        isCreated: true,
        isError: true
      });
      this.placeholderVisibilityService.hide(CommonConstant.CREATE_NEW_GROUP_HOLDER);
    });
  }

  private prepareNewGroupData() {
    const influencerGroupType = CommonConstant.PREFERRED_INFLUENCER;
    const influencerGroupNew = new InfluencerGroupNew();
    influencerGroupNew.influencerGroupId =  null;
    influencerGroupNew.influencerGroupName = this.groupName;
    influencerGroupNew.influencerGroupType = influencerGroupType;
    influencerGroupNew.influencerGroupDescription = this.groupDescription;
    influencerGroupNew.companyId = this.userService.getCompanyId();
    influencerGroupNew.menuvisible = CommonConstant.DEFAULT_MENU_VISIBLE;
    return influencerGroupNew;
  }

  cancelCreateOrUpdateGroup() {
    this.cancelCreateOrUpdateGroupEvent.emit();
  }

  get saveGroupButtonLabel() {
    return this.groupNeedToUpdate ? this.translate.instant('common.btn.update_group') : this.translate.instant('common.btn.create_new_group');
  }

  // Dealing with Textarea Height
  private calcHeight(value) {
    const numberOfLineBreaks = (value.match(/\n/g) || []).length;
    // min-height + lines x line-height + padding + border
    const newHeight = 25 + numberOfLineBreaks * 25;
    return newHeight;
  }

}



// WEBPACK FOOTER //
// ./src/app/main/influencer-groups/create-group-fragment/create-group-fragment.component.ts